import type { AromaticityPropertySet, DrawingDocument, PartialChargePropertySet } from '../model';
import { isChemicalBond } from './documentSelectors.ts';

// Mirrors contracts/molecule.schema.json (chem-molecule.v1): constitution-projection identity
// only. No depiction, no stereochemistry, and derivedPropertySets is narrowed to the two kinds
// that are constitution-only (aromaticity, partial-charge) -- CIP is stereo-dependent by
// definition (see docs/model/molecule-entity-taxonomy.md) and has no home on a bare Molecule.
export interface ChemMoleculeV1Graph {
  readonly nodes: readonly { readonly id: string }[];
  readonly edges: readonly { readonly id: string; readonly source: string; readonly target: string }[];
}

export type ChemMoleculeV1DerivedPropertySet =
  | (Omit<AromaticityPropertySet, 'structureId' | 'structureRevision'> & {
      readonly moleculeId: string;
      readonly moleculeRevision: number;
    })
  | (Omit<PartialChargePropertySet, 'structureId' | 'structureRevision'> & {
      readonly moleculeId: string;
      readonly moleculeRevision: number;
    });

export interface ChemMoleculeV1Document {
  readonly schemaVersion: 'chem-molecule.v1';
  readonly id: string;
  readonly revision: number;
  readonly graph: ChemMoleculeV1Graph;
  readonly atoms: DrawingDocument['structure']['atoms'];
  readonly bonds: DrawingDocument['structure']['bonds'];
  readonly formalCharges: DrawingDocument['structure']['formalCharges'];
  /** ADR 0026 D1: hapto bonds -- a metal and two or more ligand atoms -- kept apart from `bonds`. */
  readonly haptoBonds?: Readonly<Record<string, ChemMoleculeV1HaptoBond>>;
  readonly derivedPropertySets: readonly ChemMoleculeV1DerivedPropertySet[];
}

export interface ChemMoleculeV1HaptoBond {
  readonly metalAtomId: string;
  readonly ligandAtomIds: readonly [string, string, ...string[]];
}

export function toChemMoleculeV1(document: DrawingDocument): ChemMoleculeV1Document {
  const { structure } = document;
  // ADR 0022 D4: a hydrogen bond is drawn on the paper but is no bond of the molecule -- the
  // drawing keeps it in its depiction (moleculeDrawingProjection.ts). ADR 0026 D1: a hapto bond is
  // the molecule's, as `haptoBonds` -- never one of its bonds, which the engines read.
  const edges = structure.graph.edges.filter((edge) => isChemicalBond(structure.bonds[edge.id]));
  const haptoBonds = Object.fromEntries(
    structure.graph.edges.flatMap((edge) => {
      const bond = structure.bonds[edge.id];
      return bond?.type === 'hapto' && bond.ligandAtomIds
        ? [[edge.id, { metalAtomId: edge.source, ligandAtomIds: bond.ligandAtomIds }]]
        : [];
    })
  );
  return {
    schemaVersion: 'chem-molecule.v1',
    id: structure.id,
    revision: structure.revision,
    graph: {
      nodes: structure.graph.nodes,
      edges
    },
    atoms: structure.atoms,
    bonds:
      edges.length === structure.graph.edges.length
        ? structure.bonds
        : Object.fromEntries(edges.map((edge) => [edge.id, structure.bonds[edge.id]!])),
    formalCharges: structure.formalCharges,
    ...(Object.keys(haptoBonds).length ? { haptoBonds } : {}),
    derivedPropertySets: document.derivedPropertySets.flatMap((set) => {
      if (set.kind !== 'aromaticity' && set.kind !== 'partial-charge') return [];
      const { structureId, structureRevision, ...rest } = set;
      return [{ ...rest, moleculeId: structureId, moleculeRevision: structureRevision }];
    })
  };
}

// The same fields a search debounce should key off of: constitution only. Depiction/coordinate
// churn (panning, dragging) must not retrigger a search, and stereochemistry is excluded from
// chem-molecule.v1 entirely (#111: constitutional-exact resolves to a Molecule, not an Isomer).
export function moleculeSourceKey(document: DrawingDocument): string {
  return [
    document.structure.id,
    JSON.stringify(document.structure.graph),
    JSON.stringify(document.structure.atoms),
    JSON.stringify(document.structure.bonds),
    JSON.stringify(document.structure.formalCharges)
  ].join(':');
}
